import { Survey as SurveyJS } from 'survey-react-ui';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { NoteEvents } from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const TRACKED_QUESTIONS = {
  SESSION_OVERVIEW_PATIENT_PRESENT: 'shared_note_pt_present',
  FOLLOW_UP_MEDICAL_PROVIDER_TYPE: 'fu_med_note_provider_type',
  SESSION_OVERVIEW_WAS_PATIENT_ON_CAMERA: 'shared_note_pt_on_camera',
  FOLLOW_UP_DIETARY_SESSION_INFO_SESSION_NUMBER:
    'fu_dietary_note_session_number',
  INTAKE_MED_CONSENT_TO_VIEW_MEDICATION_HISTORY:
    'intake_med_note_med_hx_consent',
  INTAKE_MED_NOTE_PROVIDER_TYPE: 'intake_med_note_provider_type',
  MISSED_SESSION_REASON: 'missed_appt_note_reason',
  MISSED_SESSION_CANCELLATION_RESOLUTION:
    'missed_appt_note_pt_cancellation_resolution',
  MISSED_SESSION_PROVIDER_CANCELLATION_RESOLUTION:
    'missed_appt_note_provider_cancellation_resolution',
  MISSED_SESSION_CANCELLATION_STATEMENT:
    'missed_appt_note_pt_cancellation_statement',
  MISSED_SESSION_NO_SHOW_RESOLUTION: 'missed_appt_note_no_show_resolution',
  MISSED_SESSION_PROVIDER_CANCELLATION_STATEMENT:
    'missed_appt_note_provider_cancellation_statement',
  MISSED_SESSION_NO_SHOW_OUTREACH_STATEMENT:
    'missed_appt_note_no_show_outreach_statement',
  MISSED_SESSION_CANCELLATION_RESCHEDULE_DATE:
    'missed_appt_note_pt_cancellation_reschedule_date',
  MISSED_SESSION_NO_SHOW_RESCHEDULE_DATE:
    'missed_appt_note_no_show_reschedule_date',
} as const;

const templateQuestionEventMap: Partial<Record<string, NoteEvents>> = {
  [TRACKED_QUESTIONS.SESSION_OVERVIEW_PATIENT_PRESENT]:
    NoteEvents.noteTemplateSessionOverviewPatientPresenceButtonClicked,
  [TRACKED_QUESTIONS.FOLLOW_UP_MEDICAL_PROVIDER_TYPE]:
    NoteEvents.noteTemplateFollowUpMedNoteProviderTypeButtonClicked,
  [TRACKED_QUESTIONS.INTAKE_MED_NOTE_PROVIDER_TYPE]:
    NoteEvents.noteTemplateIntakeMedNoteProviderTypeButtonClicked,
  [TRACKED_QUESTIONS.SESSION_OVERVIEW_WAS_PATIENT_ON_CAMERA]:
    NoteEvents.noteTemplateSessionOverviewWasPatientOnCameraButtonClicked,
  [TRACKED_QUESTIONS.INTAKE_MED_CONSENT_TO_VIEW_MEDICATION_HISTORY]:
    NoteEvents.noteTemplateIntakeMedConsentToViewMedicationHistoryButtonClicked,
  [TRACKED_QUESTIONS.FOLLOW_UP_DIETARY_SESSION_INFO_SESSION_NUMBER]:
    NoteEvents.noteTemplateFollowUpDietarySessionInfoCBTEDropdownOptionClicked,
  [TRACKED_QUESTIONS.MISSED_SESSION_REASON]:
    NoteEvents.noteTemplateMissedSessionReasonRadioGroupSelectionClicked,
  [TRACKED_QUESTIONS.MISSED_SESSION_CANCELLATION_RESOLUTION]:
    NoteEvents.noteTemplateMissedSessionCancellationResolutionRadioGroupSelectionClicked,
  [TRACKED_QUESTIONS.MISSED_SESSION_PROVIDER_CANCELLATION_RESOLUTION]:
    NoteEvents.noteTemplateMissedSessionProviderCancellationResolutionRadioGroupSelectionClicked,
  [TRACKED_QUESTIONS.MISSED_SESSION_NO_SHOW_RESOLUTION]:
    NoteEvents.noteTemplateMissedSessionNoShowResolutionDropdownOptionClicked,
  [TRACKED_QUESTIONS.MISSED_SESSION_CANCELLATION_RESCHEDULE_DATE]:
    NoteEvents.noteTemplateMissedSessionCancellationRescheduleDateSelected,
  [TRACKED_QUESTIONS.MISSED_SESSION_NO_SHOW_RESCHEDULE_DATE]:
    NoteEvents.noteTemplateMissedSessionNoShowRescheduleDateSelected,
};

const templateQuestionFocusInEventMap: Partial<Record<string, NoteEvents>> = {
  [TRACKED_QUESTIONS.MISSED_SESSION_CANCELLATION_STATEMENT]:
    NoteEvents.noteTemplateMissedSessionCancellationStatementTextInputFocusIn,
  [TRACKED_QUESTIONS.MISSED_SESSION_NO_SHOW_OUTREACH_STATEMENT]:
    NoteEvents.noteTemplateMissedSessionNoShowOutreachStatementTextInputFocusIn,
  [TRACKED_QUESTIONS.MISSED_SESSION_PROVIDER_CANCELLATION_STATEMENT]:
    NoteEvents.noteTemplateMissedSessionProviderCancellationStatementTextInputFocusIn,
};

const PatientNoteTemplateRevisionForm = () => {
  const { note, noteTemplateFormModel } = useEditPatientNoteFormContext();
  const { trackEvent } = useAnalytics();

  return (
    <SurveyJS
      id={`note-template-form-${note.noteExternalId}`}
      model={noteTemplateFormModel}
      onFocusInQuestion={(sender, options) => {
        if (templateQuestionFocusInEventMap[options?.question?.jsonObj?.name]) {
          trackEvent(
            templateQuestionFocusInEventMap[options?.question?.jsonObj?.name],
            {
              Source: AnalyticsSourceValues.SESSION_GUIDE,
            },
          );
        }
      }}
      onValueChanged={(sender, options) => {
        if (templateQuestionEventMap[options.name]) {
          trackEvent(templateQuestionEventMap[options.name], {
            Source: AnalyticsSourceValues.SESSION_GUIDE,
          });
        }
      }}
    />
  );
};

export default PatientNoteTemplateRevisionForm;
